import { useState } from 'react';
import { Key, Shield, RefreshCw, Loader2, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import type { ProtocolConnection } from '../../types/api-types';
import type { OcpiModule } from '../../use-cases/SyncCpoOcpiDataUseCase';
import {
  getConnectionTypeName,
  getConnectionStatusText,
  getConnectionStatusColor,
} from '../../types/connection-status-types';

const OCPI_MODULES: { value: OcpiModule; label: string }[] = [
  { value: 'locations', label: 'Locations' },
  { value: 'tariffs', label: 'Tariffs' },
  { value: 'sessions', label: 'Sessions' },
  { value: 'cdrs', label: 'CDRs' },
];

interface ConnectionCardProps {
  connection: ProtocolConnection;
  isExternalCpo?: boolean;
  syncingModules?: Set<string>;
  onSyncModule?: (connectionId: number, module: OcpiModule) => void;
}

export function ConnectionCard({
  connection,
  isExternalCpo = false,
  syncingModules,
  onSyncModule,
}: ConnectionCardProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const statusColors = getConnectionStatusColor(connection.registered);
  const partyId = connection.partner?.id?.partyId || 'Unknown';
  const countryCode = connection.partner?.id?.countryCode || '';
  const name = connection.partner?.name || partyId;
  const displayId = countryCode ? `${countryCode}-${partyId}` : partyId;

  const isAnySyncing = OCPI_MODULES.some(
    (m) => syncingModules?.has(`${connection.id}:${m.value}`)
  );

  const handleSyncModule = (module: OcpiModule) => {
    setDropdownOpen(false);
    onSyncModule?.(connection.id, module);
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{getConnectionTypeName(connection.type)}</CardTitle>
            <p className="text-sm text-gray-500 mt-1">{name}</p>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusColors.bg} ${statusColors.text}`}
          >
            <span className={`w-2 h-2 rounded-full ${statusColors.dot}`} />
            {getConnectionStatusText(connection.registered)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Basic Connection Details */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500">Party ID</p>
            <p className="text-sm font-medium text-gray-900">{partyId}</p>
          </div>
          {countryCode && (
            <div>
              <p className="text-xs text-gray-500">Country Code</p>
              <p className="text-sm font-medium text-gray-900">{countryCode}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500">Internal ID</p>
            <p className="text-sm font-medium text-gray-900 font-mono">{connection.id}</p>
          </div>
        </div>

        {/* Protocol Version */}
        {connection.protocol_version && (
          <div className="pt-2 border-t">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  Protocol Version
                </p>
                <p className="text-sm font-medium text-gray-900">{connection.protocol_version}</p>
              </div>
            </div>
          </div>
        )}

        {/* Authorization Tokens */}
        {(connection.local_authorization_token || connection.remote_authorization_token) && (
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
              <Key className="h-3 w-3" />
              Authorization Tokens
            </p>
            <div className="space-y-2">
              {connection.local_authorization_token && (
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500">Local Token</p>
                  <p className="text-xs font-mono text-gray-700 break-all">
                    {connection.local_authorization_token}
                  </p>
                </div>
              )}
              {connection.remote_authorization_token && (
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500">Remote Token</p>
                  <p className="text-xs font-mono text-gray-700 break-all">
                    {connection.remote_authorization_token}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sync Now — External CPO only */}
        {isExternalCpo && onSyncModule && (
          <div className="pt-2 border-t">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDropdownOpen((o) => !o)}
                disabled={isAnySyncing}
                className="w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  {isAnySyncing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {isAnySyncing ? 'Syncing...' : 'Sync now'}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </Button>

              {dropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-md border bg-white shadow-lg">
                  {OCPI_MODULES.map((m) => {
                    const isSyncing = syncingModules?.has(`${connection.id}:${m.value}`);
                    return (
                      <button
                        key={m.value}
                        onClick={() => handleSyncModule(m.value)}
                        disabled={isSyncing || isAnySyncing}
                        className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed first:rounded-t-md last:rounded-b-md"
                      >
                        {m.label}
                        {isSyncing && <Loader2 className="h-3 w-3 animate-spin" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
